import { onUnmounted, ref } from 'vue'
import { baseApi } from '~/api/base'

/**
 * @typedef {Object} StoreProductSuggestion
 * @property {string} value - 商品 ID
 * @property {string} label - 联想项展示文本
 * @property {string} serviceId - 商品 ID 兼容字段
 * @property {string} serviceName - 商品名称
 * @property {string} modelNo - 商品型号
 */

/**
 * @description 将全局搜索商品结果转换为通用联想下拉项
 * @param {Record<string, any>} item - 全局搜索商品项
 * @returns {StoreProductSuggestion|null}
 */
function normalizeProductSuggestion(item) {
  const serviceId = item?.serviceId ?? item?.id
  if (serviceId === undefined || serviceId === null || serviceId === '') {
    return null
  }

  const modelNo = String(item?.modelNo || '').trim()
  const serviceName = String(item?.serviceName || item?.prodName || '').trim()
  const label = modelNo && serviceName ? `${modelNo} ${serviceName}` : modelNo || serviceName
  if (!label) {
    return null
  }

  return {
    ...item,
    value: String(serviceId),
    label,
    serviceId: String(serviceId),
    serviceName,
    modelNo,
  }
}

/**
 * @description 商城商品模糊联想，统一处理防抖、结果归一化和过期响应丢弃
 * @param {Object} [options] - 联想配置
 * @param {number} [options.delay=300] - 输入防抖毫秒数
 * @param {number} [options.size=10] - 单次联想数量
 * @returns {{
 *   suggestions: import('vue').Ref<StoreProductSuggestion[]>,
 *   pending: import('vue').Ref<boolean>,
 *   loading: import('vue').Ref<boolean>,
 *   scheduleSuggestions: (keyword: string) => void,
 *   clearSuggestions: () => void
 * }}
 */
export const useStoreProductSuggestions = ({ delay = 300, size = 10 } = {}) => {
  const suggestions = ref([])
  const pending = ref(false)
  const loading = ref(false)
  let debounceTimer = null
  let requestId = 0

  /**
   * @description 清空联想并使在途请求失效
   * @returns {void}
   */
  const clearSuggestions = () => {
    requestId += 1
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    suggestions.value = []
    pending.value = false
    loading.value = false
  }

  /**
   * @description 请求指定关键词的商品联想，仅最新请求允许更新状态
   * @param {string} keyword - 已清洗的搜索关键词
   * @param {number} currentRequestId - 当前请求序号
   * @returns {Promise<void>}
   */
  const fetchSuggestions = async (keyword, currentRequestId) => {
    pending.value = false
    loading.value = true
    try {
      const response = await baseApi.globalSearch({
        keyword,
        current: 1,
        size,
      })
      if (currentRequestId !== requestId) return

      const groups = Array.isArray(response) ? response : response?.data || []
      const productGroup = groups.find((item) => Number(item?.serviceType) === 1)
      const list = Array.isArray(productGroup?.globalSearchList)
        ? productGroup.globalSearchList
        : []
      suggestions.value = list.map(normalizeProductSuggestion).filter(Boolean)
    } catch (error) {
      if (currentRequestId !== requestId) return
      console.error('获取商品搜索联想失败:', error)
      suggestions.value = []
    } finally {
      if (currentRequestId === requestId) {
        loading.value = false
      }
    }
  }

  /**
   * @description 防抖调度商品联想，空关键词直接清空
   * @param {string} keyword - 原始搜索关键词
   * @returns {void}
   */
  const scheduleSuggestions = (keyword) => {
    const normalizedKeyword = String(keyword || '').trim()
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }

    requestId += 1
    const currentRequestId = requestId
    suggestions.value = []
    loading.value = false

    if (!normalizedKeyword) {
      pending.value = false
      return
    }

    pending.value = true
    debounceTimer = setTimeout(
      () => {
        debounceTimer = null
        fetchSuggestions(normalizedKeyword, currentRequestId)
      },
      Math.max(0, Number(delay) || 0)
    )
  }

  onUnmounted(() => {
    clearSuggestions()
  })

  return {
    suggestions,
    pending,
    loading,
    scheduleSuggestions,
    clearSuggestions,
  }
}
